import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  IconButton,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

export default function ImportSummaryDialog({ open, summary, onClose }) {
  if (!summary) return null;

  const { totalRecords, successCount, failureCount, successfulImports, failedImports } = summary;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#8B7355', color: 'white' }}>
        <Box>
          <Typography variant="h6">Import Complete</Typography>
          <Typography variant="caption">
            {successCount} successful • {failureCount} failed
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Summary Statistics */}
        <Box sx={{ mb: 3 }}>
          <Alert severity={failureCount === 0 ? 'success' : failureCount === totalRecords ? 'error' : 'warning'}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              Import Summary
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Records
                </Typography>
                <Typography variant="h6">{totalRecords}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Successful
                </Typography>
                <Typography variant="h6" sx={{ color: '#2e7d32' }}>
                  {successCount}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Failed
                </Typography>
                <Typography variant="h6" sx={{ color: '#d32f2f' }}>
                  {failureCount}
                </Typography>
              </Box>
            </Box>
          </Alert>
        </Box>

        {/* Successful Imports */}
        {successfulImports && successfulImports.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CheckCircleIcon sx={{ color: '#2e7d32', mr: 1 }} />
              <Typography variant="h6">
                Successful Imports ({successCount})
              </Typography>
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: '#e8f5e9', fontWeight: 600 }}>Customer</TableCell>
                    <TableCell sx={{ bgcolor: '#e8f5e9', fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ bgcolor: '#e8f5e9', fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ bgcolor: '#e8f5e9', fontWeight: 600 }}>Mode</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {successfulImports.map((txn, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {txn.customerName || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Token #{txn.customerTokenNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          ₹{txn.amount?.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(txn.transactionDate).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {txn.paymentMode || 'PDF Import'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Failed Imports */}
        {failedImports && failedImports.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ErrorIcon sx={{ color: '#d32f2f', mr: 1 }} />
              <Typography variant="h6">
                Failed Imports ({failureCount})
              </Typography>
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: '#ffebee', fontWeight: 600 }}>Customer</TableCell>
                    <TableCell sx={{ bgcolor: '#ffebee', fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ bgcolor: '#ffebee', fontWeight: 600 }}>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {failedImports.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Typography variant="body2">
                          {item.transaction?.customerName || item.transaction?.customerIdentifier || 'Unknown'}
                        </Typography>
                        {item.transaction?.customerTokenNo && (
                          <Typography variant="caption" color="text.secondary">
                            Token #{item.transaction.customerTokenNo}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          ₹{item.transaction?.amount?.toLocaleString() || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="error">
                          {item.error}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* No Results */}
        {successCount === 0 && failureCount === 0 && (
          <Alert severity="info">
            <Typography variant="body2">
              No transactions were processed.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ bgcolor: '#8B7355', '&:hover': { bgcolor: '#6d5a43' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
